import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Suspense, lazy, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { getStoredToken, isTokenExpired } from "./utils/auth";

import "./app.css";

const Navbar = lazy(() => import("./Component/Navbar/Navbar"));
const LOGIN_PATHS = new Set(["/", "/login"]);
const routesFallback = (
  <div className="w-full p-3 text-center text-sm">Loading...</div>
);
const navbarFallback = <div style={{ height: 64 }} aria-hidden="true" />;

function AppContent() {
  const location = useLocation();
  const showNavbar = !LOGIN_PATHS.has(String(location?.pathname || "").toLowerCase());
  const auth = useSelector((state) => state.user);
  const storedToken = auth?.token || getStoredToken();
  const hasValidToken = Boolean(storedToken) && !isTokenExpired(storedToken);
  const ref = useRef(null);
  const [routes, setRoutes] = useState([]);
  const [routesReady, setRoutesReady] = useState(false);

  const takeScreenshot = async () => {
    const element = ref.current;
    const { default: html2canvas } = await import("html2canvas");
    let imageData;
    await html2canvas(element).then((canvas) => {
      imageData = canvas.toDataURL();
    });
    return imageData;
  };

  useEffect(() => {
    // Optional cross-tab discovery must not make authentication depend on a
    // browser capability that can be missing or blocked in an embedded WebView.
    if (typeof BroadcastChannel !== 'function') return undefined;
    let channel;
    try { channel = new BroadcastChannel("my-app-channel"); }
    catch (_unavailableCapability) { return undefined; }
    const handleMessage = (event) => {
      if (event.data === "ping") {
        channel.postMessage("pong");
      }
    };

    channel.addEventListener("message", handleMessage);

    return () => {
      channel.removeEventListener("message", handleMessage);
      channel.close();
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    import("./exports")
      .then((mod) => {
        if (!mounted) return;
        setRoutes(mod?.default || []);
        setRoutesReady(true);
      })
      .catch(() => {
        if (!mounted) return;
        setRoutes([]);
        setRoutesReady(true);
      });
    return () => {
      mounted = false;
    };
  }, []);

  if (hasValidToken && LOGIN_PATHS.has(String(location?.pathname || "").toLowerCase())) {
    return <Navigate to="/homepage" replace />;
  }

  return (
    <div ref={ref}>
      {showNavbar ? (
        <Suspense fallback={navbarFallback}>
          <Navbar takeScreenshot={takeScreenshot} />
        </Suspense>
      ) : null}
      {routesReady ? (
        <Suspense fallback={routesFallback}>
          <Routes>
            {routes.map((c) => (
              <Route key={c.path} exact path={c.path} element={<c.component />} />
            ))}
          </Routes>
        </Suspense>
      ) : (
        routesFallback
      )}
    </div>
  );
}

function App() {
  return (
    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <AppContent />
    </BrowserRouter>
  );
}

export default App;
